import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  statesAPI,
  districtsAPI,
  areasAPI,
  type State,
  type District,
  type City,
  type Area,
  type AreaCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

export default function AreaPage() {
  const [, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<
    AreaCreatePayload & { state: string; district: string }
  >({
    name: "",
    state: "",
    district: "",
    city: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<Area | null>(null);
  const [deleting, setDeleting] = useState<Area | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchStates = async () => {
    try {
      const list = await statesAPI.list();
      setStates(Array.isArray(list) ? list : []);
    } catch {
      setStates([]);
    }
  };

  const fetchAllDistricts = async () => {
    setDistrictsLoading(true);
    try {
      const list = await districtsAPI.list();
      setDistricts(Array.isArray(list) ? list : []);
    } catch {
      setDistricts([]);
    } finally {
      setDistrictsLoading(false);
    }
  };

  const fetchCitiesByDistrict = useCallback(
    async (districtId: string | number) => {
      if (!districtId) {
        setCities([]);
        return;
      }
      setCitiesLoading(true);
      try {
        const list = await districtsAPI.getCitiesByDistrictId(districtId);
        setCities(Array.isArray(list) ? list : []);
      } catch {
        setCities([]);
      } finally {
        setCitiesLoading(false);
      }
    },
    []
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await areasAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setAreas(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load areas");
      setAreas([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchStates();
    fetchAllDistricts();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Area name is required");
      return;
    }
    if (!form.city) {
      toast.error("Please select a city");
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        city: Number(form.city) || form.city,
        is_active: form.is_active,
      };
      if (editing) {
        await areasAPI.update(editing.id, payload);
        toast.success("Area updated successfully");
      } else {
        await areasAPI.create(payload);
        toast.success("Area created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", state: "", district: "", city: "", is_active: true });
      setEditing(null);
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save area";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await areasAPI.delete(deleting.id);
      toast.success("Area deleted successfully");
      setDeleting(null);
      if (areas.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete area";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback(
    async (a: Area) => {
      setEditing(a);
      setForm({
        name: a.name,
        state: a.state !== null ? String(a.state) : "",
        district: a.district !== null ? String(a.district) : "",
        city: a.city !== null ? String(a.city) : "",
        is_active: a.is_active ?? true,
      });
      if (a.district !== null) {
        await fetchCitiesByDistrict(a.district);
      }
      setDialogOpen(true);
    },
    [fetchCitiesByDistrict]
  );

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<Area>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        extraColumns: [
          {
            id: "city",
            label: "City",
            width: 160,
            renderCell: row => row.city_name ?? "—",
          },
        ],
      }),
    [currentPage, pageSize, openEdit]
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Area</h1>
        <p className="text-muted-foreground">Manage areas</p>
      </div>
      <ListMasterPageLayout
        tableId="list-master-area"
        tableColumns={tableColumns}
        listTitle={`Areas (${totalItems})`}
        searchPlaceholder="Search areas..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({
                  name: "",
                  state: "",
                  district: "",
                  city: "",
                  is_active: true,
                });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    state: "",
                    district: "",
                    city: "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit Area" : "Create New Area"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>District *</Label>
                  <Select
                    value={String(form.district ?? "")}
                    onValueChange={v => {
                      setForm(prev => ({ ...prev, district: v, city: "" }));
                      fetchCitiesByDistrict(v);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          districtsLoading
                            ? "Loading districts..."
                            : "Select district"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map(d => (
                        <SelectItem key={d.id} value={String(d.id)}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>City *</Label>
                  <Select
                    value={String(form.city ?? "")}
                    onValueChange={v => setForm(prev => ({ ...prev, city: v }))}
                    disabled={!form.district || citiesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !form.district
                            ? "Select district first"
                            : citiesLoading
                              ? "Loading cities..."
                              : "Select city"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map(c => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="area-name">Name *</Label>
                  <Input
                    id="area-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Area name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="area-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="area-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-area"
            columns={tableColumns}
            data={areas}
            getRowKey={a => a.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No areas yet. Create one to get started."
                : "No areas found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Area</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
